import { Controller } from "@hotwired/stimulus";

/**
 * FR-11: ActionCable connection monitor for the inbox page.
 * Shows/hides a "Connection lost" banner based on ActionCable state.
 *
 * With refreshOnReconnect, a restored connection also refreshes the page,
 * since streams broadcast while it was down are gone for good.
 */
export default class InboxConnectionController extends Controller {
  static targets = ["banner"];
  static values = { refreshOnReconnect: { type: Boolean, default: false } };

  declare bannerTarget: HTMLElement;
  declare hasBannerTarget: boolean;
  declare refreshOnReconnectValue: boolean;

  private pollInterval: ReturnType<typeof setInterval> | null = null;
  private wasDisconnected = false;

  connect(): void {
    this.startMonitoring();
  }

  disconnect(): void {
    this.stopMonitoring();
  }

  dismiss(): void {
    if (this.hasBannerTarget) {
      this.bannerTarget.classList.add("hidden");
    }
  }

  private startMonitoring(): void {
    // Poll ActionCable connection state every 3 seconds
    this.pollInterval = setInterval(() => {
      this.checkConnection();
    }, 3000);
  }

  private stopMonitoring(): void {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  private checkConnection(): void {
    if (!this.hasBannerTarget) return;

    const consumer = (window as any).Turbo?.connectStreamSource
      ? this.getActionCableConsumer()
      : null;

    if (consumer && consumer.connection) {
      const isConnected = consumer.connection.isOpen();
      if (isConnected) {
        this.bannerTarget.classList.add("hidden");
        if (this.wasDisconnected && this.refreshOnReconnectValue) {
          (window as any).Turbo?.visit(window.location.href, { action: "replace" });
        }
        this.wasDisconnected = false;
      } else {
        this.bannerTarget.classList.remove("hidden");
        this.wasDisconnected = true;
      }
    }
  }

  private getActionCableConsumer(): any {
    // ActionCable consumer is available via the global ActionCable object
    return (window as any).ActionCable?.consumer;
  }
}
